import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { CURRENCIES } from '@/features/crm/deals/constants';
import { useAddRateOverride } from '../queries';

// Base rate is USD; overrides set explicit non-USD prices.
const OVERRIDE_CURRENCIES = CURRENCIES.filter((c) => c !== 'USD');

const schema = z.object({
  currency: z.enum(['EUR', 'GBP', 'AUD']),
  amount: z.string().refine((v) => v !== '' && Number(v) >= 0, 'Enter an amount ≥ 0'),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['currency', 'amount'] as const;

export function AddRateOverrideForm({ serviceId }: { serviceId: number }) {
  const add = useAddRateOverride(serviceId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currency: 'EUR', amount: '' },
  });

  async function handleSubmit(values: FormValues) {
    try {
      await add.mutateAsync(values);
      form.reset({ currency: values.currency, amount: '' });
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-4 sm:flex-row sm:items-end"
      >
        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {OVERRIDE_CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>Amount</FormLabel>
              <FormControl>
                <Input type="number" min={0} step="0.01" placeholder="0.00" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" size="sm" disabled={add.isPending}>
          <Plus /> {add.isPending ? 'Adding…' : 'Add override'}
        </Button>
      </form>
    </Form>
  );
}
